import { useContext } from 'react';
import { useQuery } from '@tanstack/react-query';
import { KnowledgeBaseHomePage } from '../components/HelpPages/pages/KnowledgeBaseHomePage';
import { createRootRoute, createRoute, Outlet } from '@tanstack/react-router';
import { KnowledgeBaseArticlePage } from '../components/HelpPages/pages/KnowledgeBaseArticlePage';
import { AppContext } from '../context/AppContext';
import { getAsideNaviData } from '../api/ajaxApi';
import { FlattenRoute, NavigationStructure } from '../types/navigation';

const episerverEdit = '/EPiServer/CMS/Content';

const generateProperUrl = (rooturl: string, url: string, elId?: number) => {
	if (url.startsWith('#')) {
		return url;
	}

	const calculatedRoot = rooturl.startsWith('/') ? rooturl : '/' + rooturl;
	const indexOfRootWithinUrl = url.indexOf(calculatedRoot);

	const calculatedUrl =
		rooturl === url ? rooturl : url.slice(indexOfRootWithinUrl, url.length);

	const calculatedUrlWithoutLastSlash = calculatedUrl.endsWith('/')
		? calculatedUrl.slice(0, -1)
		: calculatedUrl;

	if (window.location.href.includes(episerverEdit)) {
		return episerverEdit + calculatedUrlWithoutLastSlash + ',,' + elId;
	} else {
		return calculatedUrlWithoutLastSlash;
	}
};

export const useNavigationData = () => {
	const contentId = useContext(AppContext)?.pageId;
	const language = useContext(AppContext)?.language || 'en';

	const { data } = useQuery<NavigationStructure | null>({
		queryKey: ['navigationData', contentId],
		queryFn: contentId
			? async () => getAsideNaviData({ contentId, lang: language })
			: () => null,
		enabled: !!contentId,
		refetchOnMount: false,
	});

	if (!contentId || !data) {
		return { navigationData: null, routeTree: null, routes: [] };
	}

	const navigationData: NavigationStructure = {
		DisplayName: data?.DisplayName || '',
		Expanded: data?.Expanded || false,
		Id: data?.Id || 0,
		IsAnchor: data?.IsAnchor || false,
		Url: generateProperUrl(data?.Url, data?.Url, data?.Id),
		LanguageLinks: data.LanguageLinks,
		Children: [],
	};

	const flattenRoutes: FlattenRoute[] = [];

	const rootRoute = createRootRoute({
		component: () => (
			<>
				<Outlet />
			</>
		),
	});

	const epiRootUrl = data?.Url;

	flattenRoutes.push({
		id: data.Id,
		path: generateProperUrl(epiRootUrl, epiRootUrl, navigationData.Id),
		languages: data.LanguageLinks,
		type: 'index',
	});

	for (const child of data?.Children || []) {
		const childParsedUrl = generateProperUrl(epiRootUrl, child.Url, child.Id);

		flattenRoutes.push({
			id: child.Id,
			path: childParsedUrl,
			languages: child.LanguageLinks,
			type: 'child',
		});

		const navigationElement: NavigationStructure = {
			DisplayName: child.DisplayName,
			Expanded: child.Expanded,
			Id: child.Id,
			IsAnchor: child.IsAnchor,
			Url: childParsedUrl,
			LanguageLinks: child.LanguageLinks,
			Children: [],
		};
		ConfigureChildren(
			child,
			childParsedUrl,
			flattenRoutes,
			language,
			navigationElement,
			epiRootUrl,
		);

		navigationData.Children.push(navigationElement);
	}

	const routes = flattenRoutes.map((route) => {
		return createRoute({
			getParentRoute: () => rootRoute,
			path: route.path,
			component: () =>
				route.type === 'index' ? (
					<KnowledgeBaseHomePage
						pageId={route.id.toString()}
						language={language}
					/>
				) : (
					<KnowledgeBaseArticlePage
						pageId={route.id.toString()}
						language={language}
					/>
				),
		});
	});

	const routeTree = rootRoute.addChildren(routes);

	return {
		navigationData,
		routeTree,
		isEpiEdit: window.location.href.includes(episerverEdit),
		flattenRoutes,
	};
};
function ConfigureChildren(
	parent: NavigationStructure,
	childParsedUrl: string,
	flattenRoutes: FlattenRoute[],
	language: string,
	newParent: NavigationStructure,
	epiRootUrl: string,
) {
	parent.Children.forEach((subChild) => {
		const childUrlWithoutLastSlash = childParsedUrl.endsWith('/')
			? childParsedUrl.slice(0, -1)
			: childParsedUrl;
		const urlAddition = subChild.IsAnchor ? childUrlWithoutLastSlash : '';
		const subChildUrl =
			urlAddition + generateProperUrl(epiRootUrl, subChild.Url, subChild.Id);

		const element: NavigationStructure = {
			DisplayName: subChild.DisplayName,
			Expanded: subChild.Expanded,
			Id: subChild.Id,
			IsAnchor: subChild.IsAnchor,
			Url: subChildUrl,
			LanguageLinks: subChild.LanguageLinks,
			Children: [],
		};
		newParent.Children.push(element);
		if (subChild.IsAnchor) {
			return;
		}

		flattenRoutes.push({
			id: subChild.Id,
			path: generateProperUrl(epiRootUrl, subChild.Url, subChild.Id),
			languages: subChild.LanguageLinks,
			type: 'child',
		});

		ConfigureChildren(
			subChild,
			subChildUrl,
			flattenRoutes,
			language,
			element,
			epiRootUrl,
		);
	});
}
